// src/containers/reportBuilder/ReportBuilderPanel.jsx
//
// Shared Report Builder shell: tab nav (Report / Charts / Preferences /
// Graphics), the lifted prefs/includedCharts/chartSettings state, org
// report-assist preferences, and the Charts/Preferences/Graphics tabs
// themselves. Used by both the admin Report Builder page (index.js) and any
// embedded single-activity usage (e.g. the Activity page's "Custom Report"
// slideout).
//
// TAK-2050: the Charts tab is hidden entirely for embedded single-activity
// usage (`hideChartsTab`) — every chart it offers is already reachable from
// that activity's own Evaluation/Documentation pages, and the tab isn't
// needed there. It stays on the admin page, where it's used to build
// bespoke customer reports across a selection of activities.
//
// The only other real difference between the two contexts is what the
// "Report" tab shows, which is why that one tab's content is a render
// prop rather than owned here:
//
//   renderReportTab({ prefs, includedCharts, chartSettings, assistPrefs,
//                      reportAssistEnabled }) => node
//
// The admin page uses this to switch between its filter/browse panel and
// ReportPreviewSection depending on whether it's in FIND or PREVIEW mode;
// embedded usage always renders ReportPreviewSection directly, since the
// activity is fixed and there's nothing to browse.
//
// evaluationPlans/chartImages/onChartImageChange are threaded in rather than
// fetched here because they're also needed by whatever builds the Report
// tab's content via renderReportTab (ReportPreviewSection) — both call sites
// share a single useReportActivityData() hook instance, owned by whichever
// component renders this panel.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ChartsTab from './ChartsTab';
import PreferencesTab from './PreferencesTab';
import GraphicsTab from './GraphicsTab';
import './ReportBuilder.scss';

const TABS = (t) => [
  ['builder', t('report.builder.tab_labels.report')],
  ['charts', t('report.builder.tab_labels.charts')],
  ['preferences', t('report.builder.tab_labels.preferences')],
  ['graphics', t('report.builder.tab_labels.graphics')],
];

export default function ReportBuilderPanel({
  orgId,
  orgName,
  selectedActivities,
  chartFilters = {},
  evaluationPlans,
  chartImages,
  onChartImageChange,
  hideChartsTab = false,
  renderReportTab,
}) {
  const { t } = useTranslation();
  const tabs = hideChartsTab
    ? TABS(t).filter(([id]) => id !== 'charts')
    : TABS(t);
  const [activeTab, setActiveTab] = useState('builder');

  // Merged report preferences (kept in sync by PreferencesTab via onPrefsChange)
  const [prefs, setPrefs] = useState({});

  // Charts included in the report (kept in sync by ChartsTab via onIncludedChange)
  const [includedCharts, setIncludedCharts] = useState({});

  // Chart display settings — hideUnused / displayView per chart key
  const [chartSettings, setChartSettings] = useState({});

  // Bumped on tab-visibility-regain so chart panels (ChartHost, a separate
  // fetch subsystem from useReportActivityData) refetch too.
  const [chartRefreshToken, setChartRefreshToken] = useState(0);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setChartRefreshToken((t) => t + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Org-level report assistance preference — read from auth user's organisation.
  // Defaults to false: if no org setting or pref is off, WriterNote prompts and
  // AuthorComment guidance are hidden; staff write report text from scratch.
  const reportAssistEnabled = useSelector((s) =>
    Boolean(s.authentication?.user?.organisation?.reportAssistEnabled)
  );
  // Sub-preferences — each can be turned off independently when the master toggle is on.
  // All three default true (undefined → true) so enabling the master shows everything.
  const reportAssistOptions = useSelector(
    (s) => s.authentication?.user?.organisation?.reportAssistOptions
  );
  const assistPrefs = {
    enabled: reportAssistEnabled,
    showAuthorComment:
      reportAssistEnabled && reportAssistOptions?.showAuthorComment !== false,
    showWriterNote:
      reportAssistEnabled && reportAssistOptions?.showWriterNote !== false,
    showChartProse:
      reportAssistEnabled && reportAssistOptions?.showChartProse !== false,
  };

  const primarySelectedId = selectedActivities[0]?.id ?? null;

  return (
    <div className={`rb-page${activeTab === 'charts' ? ' rb-page--wide' : ''}`}>
      {/* ── Tab nav ──────────────────────────────────────────────────────────── */}
      <div className="rb-tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={`rb-tabs__tab${
              activeTab === id ? ' rb-tabs__tab--active' : ''
            }`}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Report tab — content supplied by the caller ─────────────────────── */}
      <div style={{ display: activeTab === 'builder' ? 'block' : 'none' }}>
        {renderReportTab({
          prefs,
          includedCharts,
          chartSettings,
          assistPrefs,
          reportAssistEnabled,
        })}
      </div>

      {/* ── Charts tab — omitted entirely (not just hidden) for embedded
          single-activity usage, so it never mounts or fetches chart data
          there. See hideChartsTab above. ────────────────────────────────── */}
      {!hideChartsTab && (
        <div style={{ display: activeTab === 'charts' ? 'block' : 'none' }}>
          <ChartsTab
            orgId={orgId}
            orgName={orgName}
            selectedActivities={selectedActivities}
            evaluationPlans={evaluationPlans}
            chartFilters={chartFilters}
            chartImages={chartImages}
            useGraded={prefs?.outcomes?.useGraded || false}
            refreshToken={chartRefreshToken}
            onIncludedChange={setIncludedCharts}
            onChartSettingsChange={setChartSettings}
            onChartImageChange={onChartImageChange}
          />
        </div>
      )}

      {/* ── Preferences tab ──────────────────────────────────────────────────── */}
      <div style={{ display: activeTab === 'preferences' ? 'block' : 'none' }}>
        <PreferencesTab
          orgId={orgId}
          orgName={orgName}
          activityId={primarySelectedId}
          activityIds={selectedActivities.map((a) => a.id)}
          onPrefsChange={setPrefs}
        />
      </div>

      {/* ── Graphics tab ─────────────────────────────────────────────────────── */}
      <div style={{ display: activeTab === 'graphics' ? 'block' : 'none' }}>
        <GraphicsTab orgId={orgId} orgName={orgName} />
      </div>
    </div>
  );
}
